/**
 * Lightweight typed event bus for cross-module decoupling.
 * Prefer emitting domain events over direct store mutations across module boundaries.
 */

/** @typedef {(payload: unknown) => void} EventHandler */

/** @type {Map<string, Set<EventHandler>>} */
const listeners = new Map()

/**
 * @param {string} event
 * @param {EventHandler} handler
 * @returns {() => void} unsubscribe
 */
export function on(event, handler) {
  if (!listeners.has(event)) listeners.set(event, new Set())
  listeners.get(event).add(handler)
  return () => listeners.get(event)?.delete(handler)
}

/**
 * @param {string} event
 * @param {unknown} [payload]
 */
export function emit(event, payload) {
  const set = listeners.get(event)
  if (!set) return
  for (const handler of set) {
    try {
      handler(payload)
    } catch (err) {
      console.error(`[eventBus] handler failed for ${event}`, err)
    }
  }
}

/** @param {string} event @param {EventHandler} handler */
export function off(event, handler) {
  listeners.get(event)?.delete(handler)
}

/** Clears all listeners (tests only). */
export function resetEventBusForTests() {
  listeners.clear()
}
